import { type ConnectAskResponse, type ConnectGrant, askInSpace } from "@ando/core";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Eye } from "lucide-react";
import * as React from "react";

import { ConnectChat } from "./ConnectChat.js";
import { SpaceScopeBanner } from "./SpaceScopeBanner.js";

/**
 * GuestPreviewDialog — "Anteprima come ospite".
 *
 * Opens a modal that shows the owner EXACTLY what a guest sees: the read-only
 * scope banner ("Gli ospiti interrogano SOLO: …") plus a live, scoped chat
 * preview wired to the SAME ``askInSpace`` transport the guest uses. DRY: it
 * reuses ``<SpaceScopeBanner>`` and ``<ConnectChat>`` rather than re-deriving a
 * guest view — the only thing it adds is the modal framing + a note that the
 * answers are limited to the shared grants.
 */

export interface GuestPreviewDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly spaceId: string;
  readonly spaceName: string;
  readonly grants: readonly ConnectGrant[];
}

export function GuestPreviewDialog({
  open,
  onOpenChange,
  spaceId,
  spaceName,
  grants,
}: GuestPreviewDialogProps) {
  const { i18n } = useLingui();

  const ask = React.useCallback(
    (question: string): Promise<ConnectAskResponse> => askInSpace(spaceId, { question }),
    [spaceId],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[78vh] max-h-[680px] flex-col sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <Eye size={15} strokeWidth={2} aria-hidden /> <Trans>Anteprima come ospite</Trans>
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            <Trans>
              Così appare «{spaceName}» a chi inviti. Le risposte sono limitate alle sole fonti
              condivise qui sotto.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 py-1">
          <SpaceScopeBanner grants={grants} readOnly />
          <ConnectChat
            ask={ask}
            scopeBanner={null}
            title={i18n._(msg`Anteprima ospite`)}
            subtitle={i18n._(
              msg`Le stesse risposte che vedono i tuoi ospiti, limitate alle fonti condivise.`,
            )}
            disabled={grants.length === 0}
            emptyHint={
              grants.length === 0
                ? i18n._(
                    msg`Aggiungi almeno una fonte: senza condivisioni l'ospite non può interrogare nulla.`,
                  )
                : undefined
            }
            className="min-h-0 flex-1"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
GuestPreviewDialog.displayName = "GuestPreviewDialog";
